import { formatRangeValue, rangeSummary, stopIndex, type DemandRange, type RangeBounds, type RangeUnit } from "@/lib/kanbanFilters";

interface RangeFilterProps {
  id: string;
  label: string;
  bounds: RangeBounds;
  unit: RangeUnit;
  value: DemandRange;
  onChange: (value: DemandRange) => void;
  unknownLabel: string;
}

/** Curseur à deux poignées (minimum / maximum). La borne haute veut dire « et plus ». */
export function RangeFilter({ id, label, bounds, unit, value, onChange, unknownLabel }: RangeFilterProps) {
  // Le curseur se déplace de cran en cran (index) ; les crans sont plus fins en bas de l'échelle.
  const last = bounds.stops.length - 1;
  const minIndex = stopIndex(bounds, value.min);
  const maxIndex = stopIndex(bounds, value.max);
  const ratio = (index: number) => (last > 0 ? index / last : 0);
  const valueText = (n: number, isMax: boolean) =>
    isMax && n >= bounds.max ? `${formatRangeValue(n, unit)} et plus` : formatRangeValue(n, unit);
  // Poignées collées en haut de la piste : celle du minimum passe devant pour rester attrapable.
  const minOnTop = minIndex >= last - 1;
  const disabled = value.unknownOnly;

  return (
    <div className="w-80" role="group" aria-labelledby={`${id}-label`}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span id={`${id}-label`} className="whitespace-nowrap font-medium text-gray-600">{label}</span>
        <span className="whitespace-nowrap font-semibold tabular-nums text-gray-900" aria-live="polite">
          {rangeSummary(value, bounds, unit, unknownLabel)}
        </span>
      </div>
      <div className={`range-dual mt-2 ${disabled ? "opacity-40" : ""}`}>
        <div className="range-dual-track" />
        <div
          className="range-dual-fill"
          style={{
            left: `calc(9px + ${ratio(minIndex)} * (100% - 18px))`,
            right: `calc(9px + ${1 - ratio(maxIndex)} * (100% - 18px))`,
          }}
        />
        <input
          type="range"
          min={0}
          max={last}
          step={1}
          value={minIndex}
          disabled={disabled}
          aria-label={`${label} : minimum`}
          aria-valuetext={valueText(value.min, false)}
          onChange={(event) => onChange({ ...value, min: bounds.stops[Math.min(Number(event.target.value), maxIndex)] })}
          style={{ zIndex: minOnTop ? 4 : 3 }}
        />
        <input
          type="range"
          min={0}
          max={last}
          step={1}
          value={maxIndex}
          disabled={disabled}
          aria-label={`${label} : maximum`}
          aria-valuetext={valueText(value.max, true)}
          onChange={(event) => onChange({ ...value, max: bounds.stops[Math.max(Number(event.target.value), minIndex)] })}
          style={{ zIndex: minOnTop ? 3 : 4 }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[11px] tabular-nums text-gray-400" aria-hidden="true">
        <span>{formatRangeValue(bounds.min, unit)}</span>
        <span>{formatRangeValue(bounds.max, unit)} +</span>
      </div>
      <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-gray-600">
        <input
          type="checkbox"
          checked={value.unknownOnly}
          onChange={(event) => onChange({ ...value, unknownOnly: event.target.checked })}
          className="h-3.5 w-3.5 rounded border-gray-300 accent-indigo-600"
        />
        {unknownLabel} uniquement
      </label>
    </div>
  );
}
